import CheckIcon from "@mui/icons-material/Check";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { Box, Typography } from "@mui/material";
import { format } from "date-fns";
import { baseTheme } from "../../../themes/shipinsure";

const COMPLETED_COLOR = "#2E7D32";
const INACTIVE_BG = "#94A3B8";
const INACTIVE_BORDER = "#64748B";
const INACTIVE_TEXT = "#334155";
const META_TEXT = "#1E293B";
const ICON_TRACK_HEIGHT = 40;
const CONNECTOR_INSET = "17px";

export function ClaimStepper({ steps, t }) {
  return (
    <Box
      mb="28px"
      component="nav"
      aria-label={t("status.stepper.progressLabel")}
      sx={{
        overflowX: { xs: "auto", md: "visible" },
        pb: { xs: "4px", md: 0 },
      }}
    >
      <Box width="100%">
        <Box
          display="flex"
          alignItems="center"
          position="relative"
          height={{ xs: "auto", sm: `${ICON_TRACK_HEIGHT}px` }}
          mb="6px"
        >
          <ConnectorTrack steps={steps} />
          {steps.map((step, index) => (
            <StepIconSlot
              key={step.key}
              step={step}
              isFirst={index === 0}
              isLast={index === steps.length - 1}
            />
          ))}
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={{ xs: "12px", sm: 0 }}
        >
          {steps.map((step, index) => (
            <StepLabel
              key={step.key}
              step={step}
              isFirst={index === 0}
              isLast={index === steps.length - 1}
              t={t}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function getConnectorBackground(step, nextStep) {
  if (!nextStep) {
    return INACTIVE_BG;
  }

  if (step.state === "completed" && nextStep.state === "current") {
    return `linear-gradient(90deg, ${COMPLETED_COLOR} 0%, ${baseTheme.colors.primary} 100%)`;
  }

  if (step.state === "completed") {
    return COMPLETED_COLOR;
  }

  if (step.state === "current") {
    return `linear-gradient(90deg, ${baseTheme.colors.primary} 0%, ${INACTIVE_BG} 100%)`;
  }

  return INACTIVE_BG;
}

function getConnectorPosition(index, total) {
  const left =
    index === 0
      ? CONNECTOR_INSET
      : `${((index + 0.5) / total) * 100}%`;
  const right =
    index + 1 === total - 1
      ? CONNECTOR_INSET
      : `${((total - (index + 1.5)) / total) * 100}%`;

  return { left, right };
}

function ConnectorTrack({ steps }) {
  return (
    <Box
      display={{ xs: "none", sm: "block" }}
      position="absolute"
      left={0}
      right={0}
      top="50%"
      height="3px"
      sx={{ transform: "translateY(-50%)" }}
      zIndex={0}
      aria-hidden
    >
      {steps.slice(0, -1).map((step, index) => (
        <Box
          key={`connector-${step.key}`}
          position="absolute"
          top={0}
          height="3px"
          sx={{
            ...getConnectorPosition(index, steps.length),
            background: getConnectorBackground(step, steps[index + 1]),
          }}
        />
      ))}
    </Box>
  );
}

function StepIcon({ step, isCompleted, isCurrent }) {
  if (isCompleted) {
    return <CheckIcon sx={{ fontSize: 16 }} />;
  }

  if (isCurrent && (step.key === "decision" || step.key === "claim_filed")) {
    return <FactCheckOutlinedIcon sx={{ fontSize: 16 }} />;
  }

  if (step.key.includes("ship") || step.key.includes("reship")) {
    return <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />;
  }

  return null;
}

function StepIconSlot({ step, isFirst, isLast }) {
  const isCompleted = step.state === "completed";
  const isCurrent = step.state === "current";
  const justifyContent = isFirst
    ? "flex-start"
    : isLast
      ? "flex-end"
      : "center";

  return (
    <Box
      flex={{ sm: 1 }}
      display="flex"
      justifyContent={{ xs: "flex-start", sm: justifyContent }}
      position="relative"
      zIndex={1}
      minWidth={{ sm: 0 }}
    >
      <Box
        sx={{
          width: isCurrent ? 34 : 28,
          height: isCurrent ? 34 : 28,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          backgroundColor: isCompleted
            ? COMPLETED_COLOR
            : isCurrent
              ? baseTheme.colors.primary
              : INACTIVE_BG,
          color: "#fff",
          border: isCurrent
            ? `3px solid ${baseTheme.colors.primary}`
            : isCompleted
              ? "none"
              : `2px solid ${INACTIVE_BORDER}`,
          outline: isCurrent
            ? `3px solid ${baseTheme.colors.primary}40`
            : "none",
          outlineOffset: "2px",
          boxSizing: "border-box",
        }}
        aria-hidden
      >
        <StepIcon
          step={step}
          isCompleted={isCompleted}
          isCurrent={isCurrent}
        />
      </Box>
    </Box>
  );
}

function StepLabel({ step, isFirst, isLast, t }) {
  const isCompleted = step.state === "completed";
  const isCurrent = step.state === "current";
  const stateLabel = isCompleted
    ? t("status.stepper.stepCompleted")
    : isCurrent
      ? t("status.stepper.stepCurrent")
      : t("status.stepper.stepUpcoming");

  const subtext = step.date
    ? format(new Date(step.date), "MMM d, yyyy")
    : step.subtextKey
      ? t(`status.stepper.${step.subtextKey}`)
      : null;

  const textAlign = isFirst ? "left" : isLast ? "right" : "center";

  return (
    <Box
      flex={{ sm: 1 }}
      minWidth={{ sm: 0 }}
      role="listitem"
      aria-current={isCurrent ? "step" : undefined}
      position="relative"
    >
      <Typography
        fontSize="14px"
        fontWeight={isCurrent ? 700 : 600}
        color={isCurrent ? baseTheme.colors.text : INACTIVE_TEXT}
        textAlign={{ xs: "left", sm: textAlign }}
      >
        {step.label}
      </Typography>
      {subtext ? (
        <Typography
          fontSize="14px"
          color={META_TEXT}
          textAlign={{ xs: "left", sm: textAlign }}
        >
          {subtext}
        </Typography>
      ) : null}
      <Typography
        component="span"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {stateLabel}
      </Typography>
    </Box>
  );
}
